import { type default as TombstoneReasonType } from '../types/TombstoneReasonType';
import { addMailListLog, getMailListLogObjectToAddToStore } from 'owa-mail-list-logging';
import { lazyGetHashedLogString } from 'owa-logging-utils/lib/lazyFunctions';
import type { TableView } from 'owa-mail-list-store';
import { type TableQueryType, isCopilotResultsTable } from 'owa-mail-list-store';
import { mutatorAction } from 'satcheljs';

interface TombstoneInfo {
    tombstoneReason: TombstoneReasonType;
    customData?: string; // This customData property is so we can include some data with the tombstone entry if needed. Consumers should stringify this data when adding to the tombstone map and parse it when they retrieve it.
}

interface RowKeyTombstoneMap {
    [instanceKey: string]: TombstoneInfo[];
}

interface TableViewTombstoneMap {
    [tableViewId: string]: RowKeyTombstoneMap;
}

export let tombstoneMap: TableViewTombstoneMap = {};

/**
 * TombstoneOperations is a suppression map.
 * We add to the tombstone list after doing a client operation and we want
 * to suppress ( or do special processing on) server notifications for this row until
 * we get correct notification which indicates the completion of the operation server-side
 *
 * The map is scoped as tombstoneMap[tableViewId][instanceKey]. Every keyed operation
 * (add/remove/read/count) is scoped to a single tableView so sibling tables of the same folder
 * (e.g. the All filter and the Unread filter) never share or clobber each other's tombstones.
 */

/**
 * Returns the per-tableView tombstone map for the given tableView if it exists.
 */
function getTableViewMap(tableView: TableView): RowKeyTombstoneMap | undefined {
    return tombstoneMap[tableView.id];
}

/**
 * Deletes the tableView's entry if it has no remaining tombstoned rows.
 */
function cleanUpEmptyMaps(tableViewId: string): void {
    if (tombstoneMap[tableViewId] && Object.keys(tombstoneMap[tableViewId]).length == 0) {
        delete tombstoneMap[tableViewId];
    }
}

/**
 * Add each instanceKeys in the given list to the tombstone map
 * @param instanceKeys - the instanceKeys to be added to the tombstones map
 * @param tableView - tableView these instanceKeys belong to
 * @param tombstoneReasonType - the reason for which the row is being tombstoned
 * @param jsonData - custom data to store with the tombstone entry. Must be a valid JSON string.
 */
export const addMutator: (
    instanceKeys: string[],
    tableView: TableView,
    tombstoneReasonType: TombstoneReasonType,
    jsonData?: string
) => void = mutatorAction(
    'tombstoneOperations.add',
    (
        instanceKeys: string[],
        tableView: TableView,
        tombstoneReasonType: TombstoneReasonType,
        jsonData?: string
    ): void => {
        // Tombstone is not supported in search or copilot results tables
        if (tableView.tableQuery.type == 1 || isCopilotResultsTable(tableView.tableQuery)) {
            return;
        }

        const folderId = tableView.serverFolderId;
        const tableViewId = tableView.id;

        // Create the tombstone map for the tableView if it doesn't exist
        if (!(tableViewId in tombstoneMap)) {
            tombstoneMap[tableViewId] = {};
        }

        /* eslint-disable-next-line owa-custom-rules/no-async-calls-in-mutator -- (https://aka.ms/OWALintWiki)
         * This code should be fixed by anyone touching it. - WI #324665
         *	> Mutators should not dispatch asynchronous operations
         *	> Mutators should not dispatch asynchronous operations */
        lazyGetHashedLogString.import().then(getHashedLogString => {
            addMailListLog(
                getMailListLogObjectToAddToStore('Tombstone:AddRow', {
                    folderId: getHashedLogString(folderId),
                    tableViewId,
                    rowKeys: instanceKeys.toString(),
                    addReason: tombstoneReasonType,
                })
            );
        });

        // Add to tombstone list
        const rowsTombstoneMap = tombstoneMap[tableViewId];
        for (const instanceKey of instanceKeys) {
            /**
             *  Add tombstone reason if no entry exists for this instanceKey
             */
            const existingTombstonedReasons = rowsTombstoneMap[instanceKey];
            if (!existingTombstonedReasons) {
                rowsTombstoneMap[instanceKey] = [
                    { tombstoneReason: tombstoneReasonType, customData: jsonData },
                ];
                continue;
            }

            /**
             * Override the entry if its "RowRemove" type reason
             * If a row is removed and pending delete, no row modified operation should bring it back.
             */
            if (tombstoneReasonType == 3) {
                rowsTombstoneMap[instanceKey] = [{ tombstoneReason: 3, customData: jsonData }];
                continue;
            }

            /**
             * Do not add the entry to the list if
             * there exists a RowRemove reason type entry for this row as it supercedes all other reasons
             */
            if (
                existingTombstonedReasons.findIndex(
                    tombstoneInfo => tombstoneInfo.tombstoneReason === 3
                ) > -1
            ) {
                continue;
            }

            /**
             * Add the tombstone reason to the list if
             * it does not exist in the list
             */
            if (
                existingTombstonedReasons.findIndex(
                    tombstoneInfo => tombstoneInfo.tombstoneReason === tombstoneReasonType
                ) == -1
            ) {
                existingTombstonedReasons.push({
                    tombstoneReason: tombstoneReasonType,
                    customData: jsonData,
                });
            }
        }
    }
);

/**
 * Remove given reason for given instanceKey from the tombstones map if it exists
 * @param instanceKey - the instanceKey to remove
 * @param tableView - the tableView these instanceKeys belong to
 * @param tombstoneReason reason to remove from the tombstone map
 */
export const removeMutator: (
    instanceKey: string,
    tableView: TableView,
    tombstoneReason: TombstoneReasonType
) => void = mutatorAction(
    'tombstoneOperations.remove',
    function remove(
        instanceKey: string,
        tableView: TableView,
        tombstoneReason: TombstoneReasonType
    ): void {
        /**
         * Tombstone list doesn't exist for this tableView.
         * User may have never performed scenarios that are supported by tombstone in this tableView
         */
        const rowTombstoneMap = getTableViewMap(tableView);
        if (!rowTombstoneMap) {
            return;
        }

        const tombstoneReasonsForRow = rowTombstoneMap[instanceKey];
        const indexOfTombstoneReasonToRemove = tombstoneReasonsForRow
            ? tombstoneReasonsForRow.findIndex(
                  tombstoneInfo => tombstoneInfo.tombstoneReason === tombstoneReason
              )
            : -1;
        if (indexOfTombstoneReasonToRemove == -1) {
            throw new Error(
                'tombstone.remove given reason does not exist when trying to remove entry from it'
            );
        }

        tombstoneReasonsForRow.splice(indexOfTombstoneReasonToRemove, 1);

        /**
         * Delete row's entry if there are no more reasons are tombstoned for it
         */
        if (tombstoneReasonsForRow.length == 0) {
            delete rowTombstoneMap[instanceKey];
        }

        // Delete tableView's entry if there are no more rows tombstoned in it
        cleanUpEmptyMaps(tableView.id);

        /* eslint-disable-next-line owa-custom-rules/no-async-calls-in-mutator -- (https://aka.ms/OWALintWiki)
         * This code should be fixed by anyone touching it. - WI #324665
         *	> Mutators should not dispatch asynchronous operations
         *	> Mutators should not dispatch asynchronous operations */
        lazyGetHashedLogString.import().then(getHashedLogString => {
            addMailListLog(
                getMailListLogObjectToAddToStore('Tombstone:RemoveRow', {
                    folderId: getHashedLogString(tableView.serverFolderId),
                    tableViewId: tableView.id,
                    rowKey: instanceKey,
                    removalReason: tombstoneReason,
                })
            );
        });
    }
);

/**
 * Removes all instanceKeys from the tombstone map
 */
export const removeAllMutator: () => void = mutatorAction(
    'tombstoneOperations.removeAll',
    (): void => {
        // Remove all by re-initializing entire tombstoneMap
        tombstoneMap = {};

        addMailListLog(
            getMailListLogObjectToAddToStore('Tombstone:ClearTombstoneForAllFolders', {})
        );
    }
);

/**
 * Returns the number of rows tombstoned for the given reason in the given tableView
 * @param tableView - the tableView to count tombstones for
 * @param tombstoneReasonType - the tombstone reason to count
 */
export const getCount = function getCount(
    tableView: TableView,
    tombstoneReasonType: TombstoneReasonType
): number {
    const tableViewMap = getTableViewMap(tableView);
    if (!tableViewMap) {
        return 0;
    }

    let count = 0;
    const tombstoneMapValuesForAllRows = Object.values(tableViewMap);
    for (const tombstoneEntriesForRow of tombstoneMapValuesForAllRows) {
        for (const tombstoneEntry of Object.values(tombstoneEntriesForRow)) {
            if (tombstoneEntry.tombstoneReason === tombstoneReasonType) {
                count++;
            }
        }
    }

    return count;
};

/**
 * Returns tombstoned reasons for this row
 * @param instanceKey - the instanceKey to check
 * @param tableView - the tableView for the instanceKey
 * @returns the array of TombstoneReasonType if it exists, empty array otherwise
 */
export const getTombstonedReasons = function getTombstonedReasons(
    instanceKey: string,
    tableView: TableView
): TombstoneReasonType[] {
    const rowTombstoneMap = getTableViewMap(tableView);
    const tombstoneReasonsForRow = rowTombstoneMap?.[instanceKey];
    if (tombstoneReasonsForRow) {
        return tombstoneReasonsForRow.map(tombstoneData => tombstoneData.tombstoneReason);
    }

    /**
     * This row may not have been tombstoned
     */
    return [];
};

/**
 * Returns the custom (JSON) data for a given tombstone reason if it exists
 * @param instanceKey - the instanceKey to check
 * @param tableView - the tableView for the instanceKey
 * @param tombstoneReasonType- the tombstone reason to check
 * @returns the customData corresponding to a given tombstone reason for the row if it exists, undefined otherwise
 */
export const getTombstonedReasonCustomData = function getTombstonedReasonCustomData(
    instanceKey: string,
    tableView: TableView,
    tombstoneReasonType: TombstoneReasonType
): string | undefined {
    const rowTombstoneMap = getTableViewMap(tableView);
    const tombstoneReasonsForRow = rowTombstoneMap?.[instanceKey];
    if (tombstoneReasonsForRow) {
        const index = tombstoneReasonsForRow.findIndex(
            tombstoneInfo => tombstoneInfo.tombstoneReason === tombstoneReasonType
        );
        if (index > -1) {
            return tombstoneReasonsForRow[index].customData;
        }
    }

    return undefined;
};

/**
 * Clear all instanceKeys for the given tableView from the tombstone map
 * @param tableView - the tableView whose tombstone entries should be cleared
 */
export const clearMapForTableViewMutator: (tableView: TableView) => void = mutatorAction(
    'tombstoneOperations.clearMapForTableView',
    (tableView: TableView): void => {
        if (tombstoneMap[tableView.id]) {
            delete tombstoneMap[tableView.id];
        }
    }
);

/**
 * Remove rows from tombstone for the given tableView
 * @param instanceKeys InstanceKeys for the rows to be removed from tombstone
 * @param tableView the tableView to which the rows belong
 */
export const removeRowsFromTombstoneMutator: (
    instanceKeys: string[],
    tableView: TableView
) => void = mutatorAction(
    'tombstoneOperations.removeRowsFromTombstoneMutator',
    (instanceKeys: string[], tableView: TableView): void => {
        // Tombstone list doesn't exist for this tableView.
        // User may have never performed triage operations supported by tombstone in this tableView
        const rowTombstoneMap = getTableViewMap(tableView);
        if (!rowTombstoneMap) {
            return;
        }

        for (const instanceKey of instanceKeys) {
            if (instanceKey in rowTombstoneMap) {
                delete rowTombstoneMap[instanceKey];
            }
        }

        /**
         * Delete tableView's entry if there are no more rows tombstoned in it
         */
        cleanUpEmptyMaps(tableView.id);

        /* eslint-disable-next-line owa-custom-rules/no-async-calls-in-mutator -- (https://aka.ms/OWALintWiki)
         * This code should be fixed by anyone touching it. - WI #324665
         *	> Mutators should not dispatch asynchronous operations
         *	> Mutators should not dispatch asynchronous operations */
        lazyGetHashedLogString.import().then(getHashedLogString => {
            addMailListLog(
                getMailListLogObjectToAddToStore('Tombstone:RemoveRows', {
                    folderId: getHashedLogString(tableView.serverFolderId),
                    tableViewId: tableView.id,
                    rowKeys: instanceKeys.toString(),
                })
            );
        });
    }
);
